<template>
  <div
    class="flex justify-start items-start generate-page"
    role="main"
    aria-labelledby="lyrics-generator-title"
  >
    <!-- 隐藏的页面标题，供屏幕阅读器使用 -->
    <h1 id="lyrics-generator-title" class="sr-only">
      {{ t("create.lyrics.title") || "Lyrics Generator" }}
    </h1>

    <!-- 左侧面板 -->
    <div
      class="w-full lg:w-[480px] overflow-auto lg:px-4 lg:pb-4 shadow-lg flex flex-col lg:border-r lg:border-gray-200 lg:dark:border-gray-700 border-0 h-[calc(100vh_-_4rem)] history-scroll-container"
      role="region"
      aria-label="Lyrics Generator Controls"
    >
      <!-- Tab切换 -->
      <div class="lg:pr-6 pb-4">
        <div
          class="grid grid-cols-2 lg:pt-0 rounded-full bg-gradient-to-r from-blue-900/10 to-purple-900/20 hover:bg-gradient-to-r hover:from-blue-900/20 hover:to-purple-900/30 transition-all duration-200"
          role="tablist"
          aria-label="Lyrics Generator Tabs"
        >
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="py-2 text-center text-xl font-medium text-white whitespace-nowrap"
            :class="{
              'bg-white !text-[#333] rounded-full': currentTab === tab.key,
            }"
            @click="currentTab = tab.key"
            role="tab"
            :aria-selected="currentTab === tab.key"
            :aria-controls="`${tab.key}-panel`"
            :id="`${tab.key}-tab`"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <div class="flex-1 lg:pr-4 pb-4">
        <!-- 歌词生成界面 -->
        <template v-if="currentTab === 'generate'">
          <div
            role="tabpanel"
            id="generate-panel"
            aria-labelledby="generate-tab"
          >
            <!-- 歌词输入区域 -->
            <div class="space-y-2">
              <label
                class="block text-xl py-3 px-0.5 font-medium text-gray-700 dark:text-gray-300 mb-2"
                id="description-label"
                >{{ t("create.lyrics.description.label") }}</label
              >

              <!-- 输入区域Tab切换 -->
              <div
                class="flex gap-2 pb-2"
                role="radiogroup"
                aria-labelledby="description-label"
              >
                <button
                  v-for="inputTab in inputTabs"
                  :key="inputTab.key"
                  class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 lg:hover:bg-blue-500 lg:dark:hover:bg-blue-500 transition-colors duration-200"
                  :class="{
                    'bg-blue-500 dark:bg-blue-500 text-white dark:text-white':
                      currentInputTab === inputTab.key,
                  }"
                  @click="currentInputTab = inputTab.key"
                  role="radio"
                  :aria-checked="currentInputTab === inputTab.key"
                >
                  <div
                    :class="[inputTab.icon, 'text-lg']"
                    aria-hidden="true"
                  ></div>
                  {{ t(`create.lyrics.description.${inputTab.key}.label`) }}
                </button>
              </div>

              <!-- Topics输入框 -->
              <div v-if="currentInputTab === 'topics'" class="relative px-0.5">
                <textarea
                  v-model="topics"
                  :maxlength="maxLyricsLength"
                  class="w-full"
                  :placeholder="
                    t('create.lyrics.description.topics.placeholder')
                  "
                  rows="8"
                  :disabled="isGeneratingLyrics"
                  id="topics-input"
                  aria-labelledby="description-label"
                ></textarea>
                <!-- Clear icon -->
                <div
                  v-if="topics"
                  class="absolute top-3 right-2 cursor-pointer text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
                  @click="clearText('topics')"
                  :class="{
                    'opacity-50 pointer-events-none': isGeneratingLyrics,
                  }"
                  role="button"
                  aria-label="Clear topics text"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </div>
                <div
                  class="absolute bottom-4 right-2 text-xs text-gray-400 dark:text-gray-500"
                >
                  {{ topics.length }}/{{ maxLyricsLength }}
                </div>
              </div>

              <!-- Key Word输入框 -->
              <div v-if="currentInputTab === 'keyword'" class="relative px-0.5">
                <textarea
                  v-model="keyword"
                  :maxlength="maxLyricsLength"
                  class="w-full"
                  :placeholder="
                    t('create.lyrics.description.keyword.placeholder')
                  "
                  rows="8"
                  :disabled="isGeneratingLyrics"
                  id="keyword-input"
                  aria-labelledby="description-label"
                ></textarea>
                <!-- Clear icon -->
                <div
                  v-if="keyword"
                  class="absolute top-3 right-2 cursor-pointer text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
                  @click="clearText('keyword')"
                  :class="{
                    'opacity-50 pointer-events-none': isGeneratingLyrics,
                  }"
                  role="button"
                  aria-label="Clear keyword text"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
                    ></path>
                  </svg>
                </div>
                <div
                  class="absolute bottom-4 right-2 text-xs text-gray-400 dark:text-gray-500"
                >
                  {{ keyword.length }}/{{ maxLyricsLength }}
                </div>
              </div>

              <!-- 图片上传区域 -->
              <div v-if="currentInputTab === 'image'" class="relative px-0.5">
                <div
                  class="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 text-center hover:border-blue-500 dark:hover:border-blue-500 transition-colors duration-200"
                  :class="{
                    'border-blue-500 dark:border-blue-500': isDragging,
                  }"
                  @dragenter.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @dragover.prevent
                  @drop.prevent="handleDrop"
                  @click="triggerFileInput"
                  role="button"
                  tabindex="0"
                  aria-label="Upload image"
                  @keydown.enter="triggerFileInput"
                  @keydown.space.prevent="triggerFileInput"
                >
                  <input
                    type="file"
                    ref="fileInput"
                    class="hidden"
                    accept="image/*"
                    @change="handleImageUpload"
                    aria-label="Select image file"
                  />
                  <div class="space-y-4">
                    <div class="flex justify-center">
                      <div
                        class="i-carbon-image text-6xl text-gray-400 dark:text-gray-500"
                        aria-hidden="true"
                      ></div>
                    </div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t("create.lyrics.description.image.uploadText") }}
                    </div>
                    <div v-if="imagePreview" class="mt-4">
                      <img
                        :src="imagePreview"
                        class="max-h-48 mx-auto rounded-lg"
                        :alt="imageFileName || 'Uploaded image preview'"
                      />
                      <div
                        class="mt-2 text-sm text-gray-500 dark:text-gray-400"
                      >
                        {{ imageFileName }}
                      </div>
                      <button
                        @click.stop="clearImage"
                        class="mt-2 text-sm text-red-500 hover:text-red-600"
                        aria-label="Remove uploaded image"
                      >
                        {{ t("create.lyrics.description.image.clear") }}
                      </button>
                    </div>
                    <div v-if="isUploading" class="mt-4" aria-live="polite">
                      <div
                        class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2"
                      >
                        <div
                          class="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          :style="{ width: `${uploadProgress}%` }"
                          role="progressbar"
                          :aria-valuenow="uploadProgress"
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                      <div class="text-sm text-gray-500 dark:text-gray-400">
                        {{ t("create.lyrics.description.image.uploading") }}
                        {{ Math.round(uploadProgress) }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 歌曲名称 -->
            <div class="space-y-2 mt-3">
              <label
                class="block text-xl py-3 px-0.5 font-medium text-gray-700 dark:text-gray-300 mb-2"
                for="song-name-input"
                >{{ t("create.lyrics.songName.label") }}</label
              >
              <div class="relative px-0.5">
                <input
                  v-model="songName"
                  :maxlength="maxSongNameLength"
                  class="w-full"
                  :placeholder="t('create.songName.placeholder')"
                  type="text"
                  id="song-name-input"
                />
                <!-- Clear icon -->
                <div
                  v-if="songName"
                  class="absolute top-3 right-2 cursor-pointer text-white/40 hover:text-white/20 transition-colors duration-200"
                  @click="clearText('songName')"
                  role="button"
                  aria-label="Clear song name"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
                    ></path>
                  </svg>
                </div>
                <div
                  class="absolute bottom-4 right-2 text-xs text-gray-400 dark:text-gray-500"
                >
                  {{ songName.length }}/{{ maxSongNameLength }}
                </div>
              </div>
            </div>

            <!-- 风格选择 -->
            <div class="space-y-2 mt-3">
              <div class="flex items-center justify-between">
                <label
                  class="block text-xl py-3 px-0.5 font-medium text-gray-700 dark:text-gray-300"
                  id="style-label"
                  >{{ t("create.lyrics.style.label") }}</label
                >
              </div>

              <div class="relative px-0.5">
                <textarea
                  v-model="style_str"
                  :maxlength="maxStyleLength"
                  class="w-full"
                  :placeholder="t('create.style.placeholder')"
                  rows="2"
                  aria-labelledby="style-label"
                ></textarea>
                <!-- Clear icon -->
                <div
                  v-if="style_str"
                  class="absolute top-3 right-2 cursor-pointer text-white/40 hover:text-white/20 transition-colors duration-200"
                  @click="clearText('style')"
                  role="button"
                  aria-label="Clear style text"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
                    ></path>
                  </svg>
                </div>
                <div
                  class="absolute bottom-4 right-2 text-xs text-gray-400 dark:text-gray-500"
                >
                  {{ styleDescription.length }}/{{ maxStyleLength }}
                </div>
                <button
                  class="absolute bottom-4 left-2 flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-500 dark:text-gray-400 lg:hover:text-blue-600 lg:dark:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 bg-white dark:bg-gray-800"
                  :disabled="isGeneratingStyle"
                  @click="generateRandomStyle"
                  aria-label="Generate random style"
                >
                  <div
                    class="i-carbon-shuffle text-lg"
                    aria-hidden="true"
                  ></div>
                  {{
                    isGeneratingStyle
                      ? t("create.random.generating")
                      : t("create.random.style")
                  }}
                </button>
              </div>

              <div class="relative pr-[5.5rem]">
                <div
                  ref="styleListRef"
                  class="flex gap-2 mt-2 h-12 overflow-x-auto style-list-container pb-2 hide-scrollbar pr-20"
                  @scroll="checkScrollStatus"
                  role="listbox"
                  aria-labelledby="style-label"
                >
                  <button
                    v-for="style in styleList"
                    :key="style"
                    class="flex items-center px-4 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 lg:hover:bg-gray-100 lg:dark:hover:bg-gray-700 transition-all duration-200 whitespace-nowrap flex-shrink-0"
                    @click="toggleStyle(style)"
                    role="option"
                    :aria-selected="styleDescription.includes(style)"
                  >
                    {{ style }}
                  </button>
                </div>

                <!-- 悬浮在右侧的滚动按钮 -->
                <div
                  class="absolute right-0 top-1/2 -translate-y-1/2 flex flex-end gap-1 z-10"
                >
                  <button
                    @click="scrollStyleList('left')"
                    class="w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 lg:hover:bg-gray-100 lg:dark:hover:bg-gray-700 transition-colors duration-200 shadow-md"
                    :class="{ 'opacity-50 cursor-not-allowed': !canScrollLeft }"
                    :disabled="!canScrollLeft"
                    aria-label="Scroll styles left"
                  >
                    <div
                      class="i-carbon-chevron-left text-lg"
                      aria-hidden="true"
                    ></div>
                  </button>
                  <button
                    @click="scrollStyleList('right')"
                    class="w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 lg:hover:bg-gray-100 lg:dark:hover:bg-gray-700 transition-colors duration-200 shadow-md"
                    :class="{
                      'opacity-50 cursor-not-allowed': !canScrollRight,
                    }"
                    :disabled="!canScrollRight"
                    aria-label="Scroll styles right"
                  >
                    <div
                      class="i-carbon-chevron-right text-lg"
                      aria-hidden="true"
                    ></div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Mood 选择 -->
            <div class="space-y-2 mt-4">
              <label
                class="block text-xl py-3 px-0.5 font-medium text-gray-700 dark:text-gray-300 mb-2"
                id="mood-label"
                >{{ t("create.lyrics.mood.label") }}</label
              >
              <div class="relative px-0.5">
                <textarea
                  v-model="mood_str"
                  :maxlength="maxMoodLength"
                  class="w-full"
                  :placeholder="t('create.mood.placeholder')"
                  rows="2"
                  aria-labelledby="mood-label"
                ></textarea>
                <!-- Clear icon -->
                <div
                  v-if="mood_str"
                  class="absolute top-3 right-2 cursor-pointer text-white/40 hover:text-white/20 transition-colors duration-200"
                  @click="clearText('mood')"
                  role="button"
                  aria-label="Clear mood text"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
                    ></path>
                  </svg>
                </div>
                <button
                  class="absolute bottom-4 left-2 flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-500 dark:text-gray-400 lg:hover:text-blue-600 lg:dark:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 bg-white dark:bg-gray-800"
                  :disabled="isGeneratingMood"
                  @click="generateRandomMood"
                  aria-label="Generate random mood"
                >
                  <div
                    class="i-carbon-shuffle text-lg"
                    aria-hidden="true"
                  ></div>
                  {{
                    isGeneratingMood
                      ? t("create.random.generating")
                      : t("create.random.mood")
                  }}
                </button>
              </div>

              <div class="relative pr-[5.5rem]">
                <div
                  ref="moodListRef"
                  class="flex gap-2 mt-2 h-12 overflow-x-auto style-list-container pb-2 hide-scrollbar pr-20"
                  @scroll="checkMoodScrollStatus"
                  role="listbox"
                  aria-labelledby="mood-label"
                >
                  <button
                    v-for="mood in moodList"
                    :key="mood"
                    class="flex items-center px-4 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 lg:hover:bg-gray-100 lg:dark:hover:bg-gray-700 transition-all duration-200 whitespace-nowrap flex-shrink-0"
                    @click="toggleMood(mood)"
                    role="option"
                    :aria-selected="moodDescription.includes(mood)"
                  >
                    {{ mood }}
                  </button>
                </div>

                <!-- 悬浮在右侧的滚动按钮 -->
                <div
                  class="absolute right-0 top-1/2 -translate-y-1/2 flex flex-end gap-1 z-10"
                >
                  <button
                    @click="scrollMoodList('left')"
                    class="w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 lg:hover:bg-gray-100 lg:dark:hover:bg-gray-700 transition-colors duration-200 shadow-md"
                    :class="{
                      'opacity-50 cursor-not-allowed': !canScrollMoodLeft,
                    }"
                    :disabled="!canScrollMoodLeft"
                    aria-label="Scroll moods left"
                  >
                    <div
                      class="i-carbon-chevron-left text-lg"
                      aria-hidden="true"
                    ></div>
                  </button>
                  <button
                    @click="scrollMoodList('right')"
                    class="w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 lg:hover:bg-gray-100 lg:dark:hover:bg-gray-700 transition-colors duration-200 shadow-md"
                    :class="{
                      'opacity-50 cursor-not-allowed': !canScrollMoodRight,
                    }"
                    :disabled="!canScrollMoodRight"
                    aria-label="Scroll moods right"
                  >
                    <div
                      class="i-carbon-chevron-right text-lg"
                      aria-hidden="true"
                    ></div>
                  </button>
                </div>
              </div>
            </div>

            <!-- 参考艺人 -->
            <div class="space-y-2 mt-3">
              <label
                class="block text-xl py-3 px-0.5 font-medium text-gray-700 dark:text-gray-300 mb-2"
                for="songer-name-input"
                >{{ t("create.lyrics.reference.label") }}</label
              >
              <div class="relative px-0.5">
                <input
                  v-model="songerName"
                  :maxlength="maxSongerNameLength"
                  class="w-full"
                  :placeholder="t('create.songName.placeholder')"
                  type="text"
                  id="songer-name-input"
                />
                <!-- Clear icon -->
                <div
                  v-if="songerName"
                  class="absolute top-3 right-2 cursor-pointer text-white/40 hover:text-white/20 transition-colors duration-200"
                  @click="clearText('songerName')"
                  role="button"
                  aria-label="Clear artist name"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
                    ></path>
                  </svg>
                </div>
                <div
                  class="absolute bottom-4 right-2 text-xs text-gray-400 dark:text-gray-500"
                >
                  {{ songerName.length }}/{{ maxSongerNameLength }}
                </div>
              </div>
            </div>

            <!-- 生成按钮 -->
            <div class="mt-6 px-0.5">
              <button
                @click="handleGenerateLyric"
                class="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium text-base hover:from-blue-600 hover:to-purple-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isGeneratingLyrics"
                aria-live="polite"
              >
                <div
                  v-if="isGeneratingLyrics"
                  class="inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                ></div>
                <div
                  v-else
                  class="i-carbon-pen text-xl"
                  aria-hidden="true"
                ></div>
                {{
                  isGeneratingLyrics
                    ? t("create.lyrics.generate.generating")
                    : t("create.lyrics.generate.button")
                }}
              </button>
            </div>
          </div>
        </template>

        <!-- 歌词生成记录界面 -->
        <template v-if="currentTab === 'history'">
          <div
            class="space-y-4 h-full"
            role="tabpanel"
            id="history-panel"
            aria-labelledby="history-tab"
          >
            <div
              v-if="isLoadingHistory && !lyricHistory.length"
              class="flex items-center justify-center h-full"
              aria-live="polite"
              role="status"
            >
              <div
                class="inline-block w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              ></div>
              <span class="sr-only">{{ t("common.loading") }}</span>
            </div>

            <div
              v-else-if="lyricHistory.length === 0"
              class="flex flex-col items-center justify-center h-full gap-4 text-gray-400"
            >
              <div
                class="text-6xl empty-icon i-carbon-time"
                aria-hidden="true"
              ></div>
              <div class="text-sm">{{ t("create.lyrics.history.empty") }}</div>
            </div>

            <div v-else class="space-y-4">
              <h2 class="sr-only">
                {{ t("create.lyrics.history.title") || "Lyrics History" }}
              </h2>
              <ul
                class="list-none p-0 m-0 space-y-4"
                aria-label="Lyrics History List"
              >
                <li
                  v-for="task in lyricHistory"
                  :key="task.id"
                  class="bg-gradient-to-r from-blue-900/10 to-purple-900/20 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                  role="article"
                >
                  <div class="flex items-start justify-between">
                    <h3
                      class="text-lg font-medium text-gray-900 dark:text-gray-100"
                    >
                      {{ task.lyrics_data.title }}
                    </h3>
                    <div class="flex items-center gap-2">
                      <span class="text-sm text-gray-500 dark:text-gray-400">
                        {{ new Date(task.created || "").toLocaleString() }}
                      </span>
                      <button
                        @click="viewLyrics(task)"
                        class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                        aria-label="View lyrics details"
                      >
                        <div
                          class="i-carbon-view text-xl"
                          aria-hidden="true"
                        ></div>
                      </button>
                    </div>
                  </div>

                  <div v-if="task.lyrics_data" class="mt-4">
                    <pre
                      class="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-wrap"
                      >{{
                        task.lyrics_data.text
                          .split("\\n")
                          .slice(0, 4)
                          .join("\n\n") + "..."
                      }}</pre
                    >
                  </div>
                </li>
              </ul>

              <!-- 加载更多触发器 -->
              <div
                v-if="lyricHistory.length > 0"
                class="py-10 flex flex-col items-center justify-center mt-4 min-h-[100px]"
                aria-live="polite"
              >
                <div v-if="isLoadingMore" class="flex items-center space-x-2">
                  <div
                    class="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"
                    aria-hidden="true"
                  ></div>
                  <span class="text-gray-500 dark:text-gray-400">{{
                    t("music.loading")
                  }}</span>
                </div>

                <div
                  v-else-if="!hasMoreHistory && lyricHistory.length > 0"
                  class="text-gray-500 dark:text-gray-400"
                >
                  {{ t("music.noMoreResults") }}
                </div>

                <div v-else class="text-gray-500 dark:text-gray-400">
                  {{ t("music.scrollForMore") }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 右侧面板 -->
    <div
      class="overflow-auto flex-1 lg:p-6 lg:pt-0 right-panel h-[calc(100vh_-_8rem)] lg:h-[calc(100vh_-_4rem)] lg:static lg:z-auto"
      :class="{
        'hidden lg:block': !showMobilePanel,
        'fixed inset-0 z-50 p-2 mx-4 rounded-lg mt-12 mb-20 lg:mb-0 lg:mt-0 block bg-gray-900':
          showMobilePanel,
      }"
      role="region"
      aria-label="Lyrics Preview Area"
    >
      <!-- 在移动视图下添加关闭按钮 -->
      <button
        v-if="showMobilePanel"
        @click="showMobilePanel = false"
        class="absolute top-4 right-4 bg-gray-800/70 p-2 rounded-full text-white lg:hidden"
        aria-label="close lyrics preview"
      >
        <div class="i-carbon-close text-xl"></div>
      </button>

      <!-- 生成中状态 -->
      <div
        v-if="isGeneratingLyrics"
        class="flex flex-col items-center justify-center h-full gap-4 text-blue-500 dark:text-blue-400"
        aria-live="polite"
        role="status"
      >
        <div class="flex items-end gap-2 mb-8">
          <div
            class="text-4xl animate-bounce status-icon i-carbon-music-note"
            aria-hidden="true"
          ></div>
          <div
            class="text-4xl animate-bounce status-icon i-carbon-music-note"
            style="animation-delay: 100ms"
            aria-hidden="true"
          ></div>
          <div
            class="text-4xl animate-bounce status-icon i-carbon-music-note"
            style="animation-delay: 200ms"
            aria-hidden="true"
          ></div>
        </div>

        <!-- 创作进度区域 -->
        <div
          class="flex-1 flex flex-col items-center justify-center w-full max-w-md px-4"
        >
          <!-- 文本提示 -->
          <div class="text-base font-medium mb-3 text-center">
            {{ t("create.lyrics.status.generating") }}
          </div>

          <!-- 进度条 -->
          <div
            class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-6"
          >
            <div
              class="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
              :style="{ width: `${generationProgress}%` }"
              role="progressbar"
              :aria-valuenow="generationProgress"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-label="`Generation progress: ${generationProgress}%`"
            ></div>
          </div>
        </div>

        <!-- 正在创作的状态提示 -->
        <div
          class="w-full max-w-md mx-auto bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-4 rounded-lg text-sm text-blue-300 shadow-inner"
        >
          <div class="flex items-center justify-center mb-2">
            <div class="font-medium">
              {{ t("create.lyrics.status.tips.title") }}
            </div>
          </div>
          <div class="text-center transition-all duration-300 min-h-[1.5rem]">
            <Transition name="fade" mode="out-in">
              <p :key="currentTip">{{ currentTip }}</p>
            </Transition>
          </div>
        </div>
      </div>

      <!-- 生成结果展示 -->
      <div v-else-if="currentLyricTask" class="w-full h-full flex lg:py-4 flex-col">
        <div
          class="bg-gradient-to-r from-blue-900/20 to-purple-900/30 rounded-lg p-6 mb-6 shadow-lg"
        >
          <h2 class="text-2xl font-bold text-white mb-3 leading-tight">
            {{
              currentLyricTask.lyrics_data.title ||
              t("create.lyrics.defaultTitle")
            }}
          </h2>
          <div class="flex flex-wrap gap-3 items-center">
            <div class="flex items-center text-sm text-gray-300">
              <span class="mr-2">{{ t("create.lyrics.creator") }}:</span>
              <span class="text-blue-400 font-medium">{{ userInfo.name }}</span>
            </div>
            <div
              class="w-1 h-1 bg-gray-600 rounded-full"
              aria-hidden="true"
            ></div>
            <div class="flex items-center text-sm text-gray-300">
              <span class="mr-2">{{ t("create.lyrics.generateTime") }}:</span>
              <span>{{
                new Date(currentLyricTask.created).toLocaleString()
              }}</span>
            </div>
          </div>
        </div>

        <!-- 歌词内容 -->
        <div class="flex-1 rounded-lg p-2 overflow-auto">
          <pre class="text-gray-300 whitespace-pre-wrap">{{
            currentLyricTask.lyrics_data.text.split("\\n").join("\n\n")
          }}</pre>
        </div>
      </div>

      <!-- 空状态 -->
      <div
        v-else
        class="flex flex-col items-center justify-center h-full gap-4 text-gray-400 dark:text-gray-600"
      >
        <div
          class="text-6xl empty-icon i-carbon-music"
          aria-hidden="true"
        ></div>
        <div class="text-sm font-medium">
          {{ t("create.lyrics.status.empty") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthState } from "~/composables/useAuthState";
import { useToast } from "~/composables/useToast";
import {
  API_PATHS_MUSIC,
  musicService,
  type MusicTask,
} from "~/services/musicService";
import { usePagination } from "~/composables/usePagination";

const { t } = useI18n();
const toast = useToast();
const { user, isAuthenticated, setTempUserId } = useAuthState();

useSeoMeta({
  title: t("meta.lyricsGenerator.title"),
  description: t("meta.lyricsGenerator.description"),
  keywords: t("meta.lyricsGenerator.keywords"),
  ogTitle: t("meta.lyricsGenerator.ogTitle"),
  ogDescription: t("meta.lyricsGenerator.ogDescription"),
  ogImage: "/images/ogimage.png",
  twitterCard: "summary_large_image",
});
// Tab切换
const tabs = [
  {
    key: "generate",
    label: t("create.lyrics.createLyrics"),
    icon: "i-carbon-pen",
  },
  {
    key: "history",
    label: t("create.lyrics.lyricsHistory"),
    icon: "i-carbon-time",
  },
];
const currentTab = ref("generate");

// 用户信息
const userInfo = ref<any>({
  name: "",
});

// 更新用户信息
const updateUserInfo = () => {
  if (user.value) {
    userInfo.value.name = user.value.fullName || "用户";
  }
};

// 监听用户信息变化
watch(
  user,
  () => {
    if (user.value) {
      updateUserInfo();
    }
  },
  { deep: true }
);

// 监听认证状态变化
watch(isAuthenticated, (newValue: any) => {
  if (newValue) {
    updateUserInfo();
  }
});

// 歌词生成相关
const maxLyricsLength = 3000;
const songName = ref("");
const maxSongNameLength = 60;
const songerName = ref("");
const maxSongerNameLength = 60;
const isGeneratingLyrics = ref(false);
const generationProgress = ref(0);
const currentLyricTask = ref<MusicTask | null>(null);
const showMobilePanel = ref(false); // 控制移动端面板显示

// 历史记录相关
const lyricHistory = ref<MusicTask[]>([]);
const pageSize = ref(10);

// 风格相关
const styleList = ref<any>([]);
const styleDescription = ref<any>([]);
const style_str = ref("");
const maxStyleLength = 200;
const isGeneratingStyle = ref(false);
const styleListRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);

// Mood相关
const moodList = ref<any>([]);
const moodDescription = ref<any>([]);
const mood_str = ref("");
const maxMoodLength = 200;
const isGeneratingMood = ref(false);
const moodListRef = ref<HTMLElement | null>(null);
const canScrollMoodLeft = ref(false);
const canScrollMoodRight = ref(true);

// 当前提示
const currentTip = ref("");
const currentTipIndex = ref(0);
const tipsInterval = ref<number | null>(null);

// 输入区域Tab切换
const inputTabs = [
  { key: "topics", label: "Topics", icon: "i-carbon-text" },
  { key: "keyword", label: "Key Word", icon: "i-carbon-tag" },
  { key: "image", label: "图片", icon: "i-carbon-image" },
];
const currentInputTab = ref("topics");

// 歌词生成参数
const topics = ref("");
const keyword = ref("");
const imagePreview = ref("");
const imageId = ref("");
const fileInput = ref<HTMLInputElement | null>(null);

// 图片上传相关
const isDragging = ref(false);
const isUploading = ref(false);
const uploadProgress = ref(0);
const imageFileName = ref("");

// 触发文件选择
const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

// 处理拖拽上传
const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("create.lyrics.description.image.sizeLimit"));
        return;
      }
      handleFileUpload(file);
    } else {
      toast.error(t("create.lyrics.description.image.typeError"));
    }
  }
};

// 处理文件选择
const handleImageUpload = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    const file = input.files[0];
    if (file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("create.lyrics.description.image.sizeLimit"));
        return;
      }
      handleFileUpload(file);
    } else {
      toast.error(t("create.lyrics.description.image.typeError"));
    }
  }
};

// 处理文件上传
const handleFileUpload = async (file: File) => {
  try {
    isUploading.value = true;
    uploadProgress.value = 0;
    imageFileName.value = file.name;

    // 创建本地预览
    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreview.value = e.target?.result as string;
    };
    reader.readAsDataURL(file);

    const path = isAuthenticated.value
      ? API_PATHS_MUSIC.UPLOAD.FILE
      : API_PATHS_MUSIC.UPLOAD.FILE_WITHOUT_AUTH;
    // 上传文件
    let res = await musicService.uploadFile(path, file, (progress) => {
      uploadProgress.value = progress;
    });

    if (res.data.id) {
      imageId.value = res.data.id;
      toast.success(t("create.lyrics.description.image.success"));
      // 这里可以处理上传成功后的逻辑，比如保存图片URL等
    } else {
      throw new Error(res.message || "上传失败");
    }
  } catch (error) {
    console.error("上传失败:", error);
    toast.error(t("create.lyrics.description.image.failed"));
    clearImage();
  } finally {
    isUploading.value = false;
    uploadProgress.value = 0;
  }
};

// 清除图片
const clearImage = () => {
  imagePreview.value = "";
  imageFileName.value = "";
  imageId.value = "";
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

// 加载风格和心情数据
const loadStyleData = async () => {
  try {
    // 加载风格数据
    const res: any = await musicService.getStyleList();
    styleList.value = res.data.list;

    // 加载心情数据
    const moodRes: any = await musicService.getMoodList();
    moodList.value = moodRes.data.list;

    // 在数据加载后检查滚动状态
    nextTick(() => {
      checkScrollStatus();
      checkMoodScrollStatus();
    });
  } catch (error) {
    console.error("加载数据失败:", error);
    styleList.value = [];
    moodList.value = [];
    toast.error(t("lyric.toast.loadFailed"));
  }
};

// 检查滚动状态
const checkScrollStatus = () => {
  if (!styleListRef.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = styleListRef.value;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 5;
};

// 检查 mood 滚动状态
const checkMoodScrollStatus = () => {
  if (!moodListRef.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = moodListRef.value;
  canScrollMoodLeft.value = scrollLeft > 0;
  canScrollMoodRight.value = scrollLeft < scrollWidth - clientWidth - 5;
};

// 滚动风格列表
const scrollStyleList = (direction: "left" | "right") => {
  if (!styleListRef.value) return;

  const scrollAmount = 300;
  const currentScroll = styleListRef.value.scrollLeft;

  if (direction === "left") {
    styleListRef.value.scrollLeft = Math.max(0, currentScroll - scrollAmount);
  } else {
    styleListRef.value.scrollLeft = Math.min(
      styleListRef.value.scrollWidth - styleListRef.value.clientWidth,
      currentScroll + scrollAmount
    );
  }

  nextTick(() => {
    checkScrollStatus();
  });
};

// 滚动 mood 列表
const scrollMoodList = (direction: "left" | "right") => {
  if (!moodListRef.value) return;

  const scrollAmount = 300;
  const currentScroll = moodListRef.value.scrollLeft;

  if (direction === "left") {
    moodListRef.value.scrollLeft = Math.max(0, currentScroll - scrollAmount);
  } else {
    moodListRef.value.scrollLeft = Math.min(
      moodListRef.value.scrollWidth - moodListRef.value.clientWidth,
      currentScroll + scrollAmount
    );
  }

  nextTick(() => {
    checkMoodScrollStatus();
  });
};

// 切换风格选择
const toggleStyle = (item: any) => {
  styleDescription.value.push(item);
  style_str.value = styleDescription.value.join(",");
};

// 切换 mood 选择
const toggleMood = (item: any) => {
  moodDescription.value.push(item);
  mood_str.value = moodDescription.value.join(",");
};

// 随机生成风格
const generateRandomStyle = async () => {
  try {
    isGeneratingStyle.value = true;

    const randomIndex = Math.floor(Math.random() * styleList.value.length);
    const randomStyle = styleList.value[randomIndex];

    styleDescription.value.length = 0;
    styleDescription.value.push(randomStyle);
    style_str.value = randomStyle;
    await new Promise((resolve) => setTimeout(resolve, 300));
  } catch (error) {
    console.error("生成风格失败:", error);
    toast.error(t("lyric.toast.styleFailed"));
  } finally {
    isGeneratingStyle.value = false;
  }
};

// 随机生成 mood
const generateRandomMood = async () => {
  try {
    isGeneratingMood.value = true;

    const randomIndex = Math.floor(Math.random() * moodList.value.length);
    const randomMood = moodList.value[randomIndex];

    moodDescription.value.length = 0;
    moodDescription.value.push(randomMood);
    mood_str.value = randomMood;
    await new Promise((resolve) => setTimeout(resolve, 300));
  } catch (error) {
    console.error("生成心情失败:", error);
    toast.error(t("lyric.toast.moodFailed"));
  } finally {
    isGeneratingMood.value = false;
  }
};

// 修改清除文本方法
const clearText = (
  type: "topics" | "keyword" | "style" | "songName" | "mood" | "songerName"
) => {
  switch (type) {
    case "topics":
      topics.value = "";
      break;
    case "keyword":
      keyword.value = "";
      break;
    case "style":
      style_str.value = "";
      styleDescription.value = [];
      break;
    case "mood":
      mood_str.value = "";
      moodDescription.value = [];
      break;
    case "songName":
      songName.value = "";
      break;
    case "songerName":
      songerName.value = "";
      break;
    default:
      break;
  }
};

// 获取本地历史记录
const getLocalHistory = () => {
  return musicService.getLocalLyricHistory();
};

// 获取远程历史记录
const fetchRemoteHistory = async (pageNum: number) => {
  return await musicService.getLyricHistory({
    page: pageNum,
    size: pageSize.value,
  });
};

// 使用分页hooks获取历史记录
const {
  list: historyList,
  isLoading: isLoadingHistory,
  isLoadingMore,
  hasMore: hasMoreHistory,
  error,
  loadMore,
  loadData: loadHistoryData,
} = usePagination<MusicTask>(
  async (pageNum: number) => {
    return await fetchRemoteHistory(pageNum);
  },
  {
    pageSize: pageSize.value,
    autoLoad: false,
    dataExtractor: (response) => {
      if ((response as any).code === 401) {
        toast.error(t("user.sessionExpired"));
        return [];
      }
      // 提取任务列表
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (typeof response.data === "object" && "list" in response.data) {
        return response.data.list;
      }
      return [];
    },
    errorHandler: async (err) => {
      if ((err as any).response && (err as any).response.status === 401) {
        toast.error(t("user.sessionExpired"));
      }
    },
  }
);

// 监听历史记录列表变化
watch(historyList, (newList) => {
  lyricHistory.value = newList;
});

// 加载历史记录
const loadLyricHistory = async () => {
  if (isAuthenticated.value) {
    await loadHistoryData(1);
  } else {
    lyricHistory.value = getLocalHistory();
    hasMoreHistory.value = false;
  }
};

// 加载更多历史记录
const loadMoreHistory = async () => {
  if (isLoadingHistory.value || !hasMoreHistory.value || !isAuthenticated.value)
    return;
  try {
    await loadMore();
  } catch (error) {
    console.error("加载更多失败:", error);
  }
};

// 监听滚动到底部
const handleScroll = () => {
  const container = document.querySelector(".history-scroll-container");
  if (!container) return;

  const { scrollTop, scrollHeight, clientHeight } = container;
  if (scrollHeight - scrollTop - clientHeight < 100) {
    loadMoreHistory();
  }
};

// 获取任务结果
const getLyricTaskResult = async (taskId: string) => {
  try {
    if (!taskId) return;

    const path = isAuthenticated.value
      ? API_PATHS_MUSIC.TASK.RESULT
      : API_PATHS_MUSIC.TASK.RESULT_WITHOUT_AUTH;
    let res = await musicService.getMusicTaskResult({ task_id: taskId }, path);

    if (res.data.status < 3) {
      // 任务未完成,继续轮询
      setTimeout(() => {
        getLyricTaskResult(taskId);
      }, 3000);
    } else if (res.data.status === 3) {
      // 任务完成
      currentLyricTask.value = res.data;
      isGeneratingLyrics.value = false;
      generationProgress.value = 100;
      toast.success(t("create.lyrics.generate.success"));

      // 保存到本地存储
      musicService.saveLyricHistory(res.data);

      // 刷新历史记录
      loadLyricHistory();
    } else {
      // 任务失败
      currentLyricTask.value = res.data;
      isGeneratingLyrics.value = false;
      toast.error(res.data.fail_reason || t("create.lyrics.generate.failed"));
    }
  } catch (error) {
    console.error("获取任务结果失败:", error);
    isGeneratingLyrics.value = false;
    toast.error("获取任务结果失败");
  }
};

// 生成歌词前的参数检查
const handleGenerateLyric = async () => {
  // 检查至少有一个输入参数
  if (!topics.value.trim() && !keyword.value.trim() && !imageId.value) {
    toast.error(t("create.lyrics.generate.noInput"));
    return;
  }

  // 调用生成歌词方法
  await generateLyrics();
};

const getTags = () => {
  return `${style_str.value}${mood_str.value ? "," + mood_str.value : ""}`;
};
// 修改生成歌词方法
const generateLyrics = async () => {
  // 设置生成中状态
  isGeneratingLyrics.value = true;
  generationProgress.value = 0;
  currentLyricTask.value = null;

  try {
    const path = isAuthenticated.value
      ? API_PATHS_MUSIC.LYRICS.GENERATE
      : API_PATHS_MUSIC.LYRICS.GENERATE_WITHOUT_AUTH;
    let res = await musicService.generateLyrics(path, {
      topics: topics.value,
      keyword: keyword.value,
      image: imageId.value,
      title: songName.value,
      style: style_str.value,
      moods: mood_str.value,
      songer_name: songerName.value,
    });

    const taskId = res.data.task_id;
    if (taskId) {
      toast.success(t("create.lyrics.generate.taskSubmitted"));

      // 设置临时用户id, 将来用于关联用户，将歌词生成结果与用户关联
      // const userId = res.data.user_id;
      // if (userId) {
      //   setTempUserId(userId);
      // }
      // 开始轮询任务结果
      getLyricTaskResult(taskId);
    } else {
      isGeneratingLyrics.value = false;
      toast.error(t("create.lyrics.generate.taskFailed"));
    }
  } catch (error) {
    console.error("生成歌词失败:", error);
    isGeneratingLyrics.value = false;
    toast.error(t("create.lyrics.generate.resultFailed"));
  }
};

// 查看歌词
const viewLyrics = (task: MusicTask) => {
  currentLyricTask.value = task;
  showMobilePanel.value = true; // 显示面板
};

// 开始轮播提示
const startTipsRotation = () => {
  // 清除可能存在的旧定时器
  if (tipsInterval.value) {
    clearInterval(tipsInterval.value);
  }

  // 设置新的定时器，每2秒切换一次提示
  tipsInterval.value = window.setInterval(() => {
    currentTipIndex.value = (currentTipIndex.value + 1) % 5;
    currentTip.value = t(
      `create.lyrics.status.tips.tip${currentTipIndex.value + 1}`
    );
  }, 4000);

  // 立即显示第一条提示
  currentTip.value = t("create.lyrics.status.tips.tip1");
};

// 停止轮播提示
const stopTipsRotation = () => {
  if (tipsInterval.value) {
    clearInterval(tipsInterval.value);
    tipsInterval.value = null;
  }
};

// 监听生成状态变化
watch(isGeneratingLyrics, (newValue) => {
  if (newValue) {
    startTipsRotation();
  } else {
    stopTipsRotation();
    currentTip.value = "";
    currentTipIndex.value = 0;
  }
});

// 组件卸载时清理定时器
onUnmounted(() => {
  stopTipsRotation();
});

// 初始化
onMounted(() => {
  updateUserInfo();
  loadStyleData();
  loadLyricHistory();

  const container = document.querySelector(".history-scroll-container");
  if (container) {
    container.addEventListener("scroll", handleScroll);
  }
});

// 监听tab切换
watch(currentTab, (newTab) => {
  if (newTab === "history") {
    loadLyricHistory();
  }
});
</script>

<style scoped>
/* 添加过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 隐藏滚动条但保留滚动功能 */
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.style-list-container {
  scroll-behavior: smooth;
  position: relative;
  padding-bottom: 8px;
}

@media (max-width: 768px) {
  .style-list-container {
    padding-right: 16px;
  }

  .style-list-container::after {
    width: 30px;
    right: 16px;
  }

  button:hover,
  a:hover {
    @apply bg-none shadow-none;
  }

  textarea:focus,
  input:focus {
    @apply border-blue-500 dark:border-blue-500 ring-1 ring-blue-500 dark:ring-blue-500;
    outline: none;
  }
}
</style>
